import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { api, apiConfig } from "../api/api";
import FeedbackModal from "../feedback/FeedbackModal";
import Notify from "../ui/Notify";

const Row = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  cursor: pointer;
  margin-top: 0.9rem;
`;

const Check = styled.input`
  /* Touch-first: v3 runs on tablets at the counter, and a default 13px
     checkbox is not a reliable target with a finger. */
  width: 20px;
  height: 20px;
  min-width: 20px;
  margin-top: 2px;
`;

const Link = styled.a`
  text-decoration: underline;
`;

const Version = styled.div`
  margin-top: 0.75rem;
  font-size: 0.78rem;
  opacity: 0.7;
`;

/**
 * The clickwrap in front of every purchase.
 *
 * A published Terms of Service that nobody can be shown to have accepted is
 * not enforceable, so this component exists to produce evidence, not to show
 * text: an unticked box, a real link to the document, and a server-side record
 * of who ticked it, when, and against which exact version.
 *
 * The box is never pre-ticked and the button stays disabled until it is. That
 * is not politeness — a pre-ticked box is the single most common reason a
 * clickwrap gets thrown out.
 *
 * Acceptance is recorded BEFORE the payment flow opens. If recording fails we
 * stop and say so rather than letting the sale through: a subscription with no
 * accepted terms behind it is the exact situation this was built to prevent.
 */
const TermsGate = ({ show, onHide, onAccepted, moduleCode }) => {
  const { t } = useTranslation();

  const [doc, setDoc] = React.useState(null);
  const [checked, setChecked] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  // Re-fetch each time it opens, so a document replaced mid-session is the one
  // the customer actually sees and the one whose hash gets recorded.
  React.useEffect(() => {
    if (!show) {
      setChecked(false);
      setDoc(null);
      return;
    }
    let cancelled = false;
    api
      .ajax("legal/document", {
        noWorker: true,
        url: apiConfig.appUrl,
        type: "GET",
        params: { document: "terms_of_service" },
      })
      .then((res) => {
        if (!cancelled) setDoc(res);
      })
      .catch(() => {
        if (!cancelled) {
          Notify(t("legal.load_failed"), "danger", 8000);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [show, t]);

  const accept = () => {
    if (!checked || !doc || busy) return;
    setBusy(true);
    api
      .ajax("legal/accept", {
        noWorker: true,
        url: apiConfig.appUrl,
        type: "POST",
        data: {
          document: "terms_of_service",
          version: doc.version,
          hash: doc.hash,
          context: "subscribe_module",
          moduleCode: moduleCode || null,
        },
      })
      .then(() => {
        setBusy(false);
        // Accepted first: onHide means "backed out" to the caller, which drops
        // the purchase it was holding — called first, the checkout never opened.
        onAccepted && onAccepted();
        onHide && onHide();
      })
      .catch(() => {
        setBusy(false);
        // Deliberately does NOT continue to checkout.
        Notify(t("legal.accept_failed"), "danger", 8000);
      });
  };

  const documentUrl =
    apiConfig.appUrl + "legal/document?document=terms_of_service";

  return (
    <FeedbackModal
      show={show}
      onHide={onHide}
      severity="info"
      title={t("legal.title")}
      message={
        <div>
          <div>{t("legal.intro")}</div>
          <Row>
            <Check
              type="checkbox"
              checked={checked}
              disabled={!doc}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span>
              {t("legal.checkbox_prefix")}{" "}
              <Link href={documentUrl} target="_blank" rel="noreferrer">
                {t("legal.terms_link")}
              </Link>
              {t("legal.checkbox_suffix")}
            </span>
          </Row>
          {doc ? (
            <Version>
              {t("legal.version", { version: doc.version })}
            </Version>
          ) : null}
        </div>
      }
      secondaryAction={{
        label: t("legal.cancel"),
        onClick: () => onHide && onHide(),
      }}
      primaryAction={{
        label: busy ? t("legal.saving") : t("legal.continue"),
        onClick: accept,
        disabled: !checked || !doc || busy,
      }}
    />
  );
};

export default TermsGate;
